import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Send, User, BadgeCheck, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Messages = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [showManifestoDialog, setShowManifestoDialog] = useState(false);
  const [manifestoAgreed, setManifestoAgreed] = useState(false);
  const [pendingMessage, setPendingMessage] = useState("");

  useEffect(() => {
    fetchMessages();
    if (userId) {
      fetchConversation(userId);
    }
  }, [userId]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`, {
        withCredentials: true,
      });
      const allMessages = response.data;

      // Group messages by conversation partner
      const conversationMap = new Map();
      allMessages.forEach((msg) => {
        const partnerId =
          msg.sender_id === currentUser.user_id
            ? msg.recipient_id
            : msg.sender_id;
        const partnerName =
          msg.sender_id === currentUser.user_id
            ? "Unknown"
            : msg.sender_name;

        if (!conversationMap.has(partnerId)) {
          conversationMap.set(partnerId, {
            user_id: partnerId,
            name: partnerName,
            lastMessage: msg.content,
            created_at: msg.created_at,
          });
        }
      });

      setConversations(Array.from(conversationMap.values()));
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (partnerId) => {
    try {
      const response = await axios.get(`${API}/messages/${partnerId}`, {
        withCredentials: true,
      });
      setMessages(response.data);

      // Try to get user profile
      try {
        const profileResponse = await axios.get(`${API}/profile/${partnerId}`, {
          withCredentials: true,
        });
        setSelectedUser(profileResponse.data);
      } catch (e) {
        setSelectedUser({ user_id: partnerId, name: "Member" });
      }
    } catch (error) {
      toast.error("Failed to load conversation");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    // Check if manifesto has been agreed to for this session
    if (!manifestoAgreed) {
      setPendingMessage(newMessage);
      setShowManifestoDialog(true);
      return;
    }

    await sendMessage(newMessage);
  };

  const sendMessage = async (content) => {
    try {
      await axios.post(
        `${API}/messages`,
        {
          recipient_id: userId,
          content: content,
          manifesto_agreed: true,
        },
        { withCredentials: true }
      );

      setNewMessage("");
      setPendingMessage("");
      fetchConversation(userId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    }
  };

  const handleManifestoAgree = () => {
    setManifestoAgreed(true);
    setShowManifestoDialog(false);
    if (pendingMessage) {
      sendMessage(pendingMessage);
    }
  };

  const ManifestoDialog = () => (
    <Dialog open={showManifestoDialog} onOpenChange={setShowManifestoDialog}>
      <DialogContent className="bg-ivory border-border max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-h3 text-charcoal flex items-center gap-2">
            <Shield className="w-6 h-6 text-gold" />
            The No-Dating Manifesto
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-6">
          <p className="text-body text-muted-foreground">
            Before you can message other members, you must agree to our
            platonic-only policy. This is what makes Havns different.
          </p>

          <div className="bg-white border border-border p-6 space-y-4">
            <p className="text-body font-medium text-charcoal">
              By sending this message, I affirm that:
            </p>
            <ul className="space-y-3 text-body text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-gold">•</span>
                I will not pursue romantic or dating relationships
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold">•</span>
                I understand this is a platonic-only community
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold">•</span>
                I will be reported and removed for romantic advances
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold">•</span>
                I respect others' boundaries and independence
              </li>
            </ul>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="manifesto-agree"
              checked={manifestoAgreed}
              onCheckedChange={setManifestoAgreed}
              data-testid="manifesto-checkbox"
            />
            <label
              htmlFor="manifesto-agree"
              className="text-sm text-charcoal cursor-pointer"
            >
              I have read and agree to the No-Dating Manifesto. I understand
              violation will result in immediate removal.
            </label>
          </div>

          <Button
            onClick={handleManifestoAgree}
            disabled={!manifestoAgreed}
            className="btn-primary w-full"
            data-testid="agree-manifesto-btn"
          >
            I Agree — Send Message
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="min-h-screen bg-ivory">
      {/* Header */}
      <header className="border-b border-border bg-ivory/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container-luxury flex items-center justify-between h-20">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-charcoal hover:text-gold transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-caption">Back to Dashboard</span>
          </button>
          <h1 className="font-serif text-xl text-charcoal">Messages</h1>
          <div className="w-24" /> {/* Spacer */}
        </div>
      </header>

      <main className="container-luxury py-8">
        <div className="grid grid-cols-12 gap-8 min-h-[600px]">
          {/* Conversations List */}
          <div className="col-span-12 md:col-span-4 bg-white border border-border">
            <div className="p-4 border-b border-border">
              <p className="text-caption">Conversations</p>
            </div>
            <ScrollArea className="h-[500px]">
              {loading ? (
                <div className="p-8 text-center">
                  <p className="text-caption">Loading...</p>
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-body text-muted-foreground">
                    No conversations yet
                  </p>
                  <p className="text-caption mt-2">
                    Visit a profile to start messaging
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {conversations.map((conv) => (
                    <button
                      key={conv.user_id}
                      onClick={() => navigate(`/messages/${conv.user_id}`)}
                      className={`w-full p-4 text-left hover:bg-secondary/50 transition-colors ${
                        userId === conv.user_id ? "bg-secondary" : ""
                      }`}
                      data-testid={`conversation-${conv.user_id}`}
                    >
                      <p className="font-medium text-charcoal">{conv.name}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {conv.lastMessage}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className="col-span-12 md:col-span-8 bg-white border border-border flex flex-col">
            {userId && selectedUser ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-border flex items-center gap-3">
                  <button
                    onClick={() => navigate(`/profile/${selectedUser.user_id}`)}
                    className="flex items-center gap-3 hover:opacity-80"
                    data-testid="chat-user-profile"
                  >
                    {selectedUser.picture ? (
                      <img
                        src={selectedUser.picture}
                        alt={selectedUser.name}
                        className="w-10 h-10 object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                        <User className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-charcoal">
                          {selectedUser.name}
                        </span>
                        {selectedUser.is_verified && (
                          <BadgeCheck className="w-4 h-4 text-gold" />
                        )}
                      </div>
                      <p className="text-caption">{selectedUser.profession}</p>
                    </div>
                  </button>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  {messages.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <Shield className="w-12 h-12 text-gold mx-auto mb-4" strokeWidth={1} />
                        <p className="text-body-lg">Start a platonic conversation</p>
                        <p className="text-caption mt-2">
                          Remember: This is a no-dating community
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.message_id}
                          className={`flex ${
                            msg.sender_id === currentUser.user_id
                              ? "justify-end"
                              : "justify-start"
                          }`}
                        >
                          <div
                            className={`max-w-[70%] p-4 ${
                              msg.sender_id === currentUser.user_id
                                ? "bg-charcoal text-ivory"
                                : "bg-secondary text-charcoal"
                            }`}
                          >
                            <p className="text-body">{msg.content}</p>
                            <p className="text-xs mt-2 opacity-60">
                              {new Date(msg.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>

                {/* Message Input */}
                <div className="p-4 border-t border-border">
                  <div className="flex gap-4">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 input-luxury border border-border"
                      onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                      data-testid="message-input"
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      className="btn-primary"
                      data-testid="send-message-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                  <p className="text-body-lg">Select a conversation</p>
                  <p className="text-caption mt-2">
                    Or visit a member's profile to start messaging
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <ManifestoDialog />
    </div>
  );
};

export default Messages;
